// TODO: Re-integrate export dialogs with workspace store
// import { ExportSocialAdDialog } from '@/components/canvas-workspace/ExportSocialAdDialog';
// import { ExportEmailDialog } from '@/components/canvas-workspace/ExportEmailDialog';
import type { SceneDocument } from '@/lib/scene-document';

import { useTranslation } from 'react-i18next';
import { paths } from 'src/routes/paths';
import { useQueryClient } from '@tanstack/react-query';
import { useOrganization } from '@/context/organization-context';
import EmailBuilder from '@/components/email-builder/EmailBuilder';
import { useNavigate, createFileRoute } from '@tanstack/react-router';
import { useRef, useMemo, useState, useEffect, useCallback } from 'react';
import { setFontFamilies, setFontsImportUrl } from '@/lib/canvaskit-init';
import { WorkspaceToolbar } from '@/components/canvas-workspace/WorkspaceToolbar';
import { WorkspaceChatPanel } from '@/components/canvas-workspace/WorkspaceChatPanel';
import { ScenePropertiesPanel } from '@/components/canvas-editor/ScenePropertiesPanel';
import { EMAIL_WIDTH, EMAIL_MIN_HEIGHT } from '@/components/canvas-editor/canvas-artboard';
import { WorkspaceInfiniteCanvas } from '@/components/canvas-workspace/WorkspaceInfiniteCanvas';
import { useBrandsControllerGetClientBrandProfile } from '@/apis/backend/generated/brands/brands';
import { useTemplatesControllerListTemplates } from '@/apis/backend/generated/templates/templates';
import {
  mergeWorkspaceToSingleDoc,
} from '@/components/canvas-workspace/workspace-canvas-store';
import {
  useWorkspaceStore,
  useWorkspaceStoreApi,
  WorkspaceEditorProvider,
} from '@/components/canvas-workspace/WorkspaceEditorProvider';
import {
  useWorkspacesControllerGetWorkspace,
  useWorkspacesControllerUpdateCanvas,
  useWorkspacesControllerCreateCanvas,
  useWorkspacesControllerDeleteCanvas,
  useWorkspacesControllerUpdateWorkspace,
  useWorkspacesControllerImportFromTemplate,
  getWorkspacesControllerGetWorkspaceQueryKey,
} from '@/apis/backend/generated/workspaces/workspaces';

import AddIcon from '@mui/icons-material/Add';
import SaveIcon from '@mui/icons-material/Save';
import ArticleIcon from '@mui/icons-material/Article';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import {
  Box,
  Tab,
  Chip,
  Tabs,
  Stack,
  Button,
  Dialog,
  Divider,
  Snackbar,
  TextField,
  IconButton,
  Typography,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
} from '@mui/material';

export const Route = createFileRoute(
  '/_authed/dashboard/o/$orgId/c/$clientId/workspaces/$workspaceId',
)({
  component: WorkspacePage,
});

// ── Canvas presets for the "Add Frame" dialog ──

interface CanvasPreset {
  label: string;
  type: 'email' | 'social_ad' | 'video';
  width: number;
  height: number;
  metadata: Record<string, any>;
  disabled?: boolean;
}

const CANVAS_PRESETS: CanvasPreset[] = [
  // Email frames hug their content (auto height); the preset height is just
  // the empty-frame floor (EMAIL_MIN_HEIGHT), not a fixed page size.
  { label: 'Email', type: 'email', width: EMAIL_WIDTH, height: EMAIL_MIN_HEIGHT, metadata: {} },
  { label: 'IG Square', type: 'social_ad', width: 1080, height: 1080, metadata: { platform: 'instagram', format: 'square', adSizeId: 'ig-square' } },
  { label: 'IG Story', type: 'social_ad', width: 1080, height: 1920, metadata: { platform: 'instagram', format: 'story', adSizeId: 'ig-story' } },
  { label: 'FB Post', type: 'social_ad', width: 1200, height: 630, metadata: { platform: 'facebook', format: 'post', adSizeId: 'fb-landscape' } },
  { label: 'Pinterest Pin', type: 'social_ad', width: 1000, height: 1500, metadata: { platform: 'pinterest', format: 'pin', adSizeId: 'pinterest-pin' } },
  { label: 'IG Reel', type: 'video', width: 1080, height: 1920, metadata: { platform: 'instagram', format: 'reel', duration: 15 }, disabled: true },
  { label: 'TikTok', type: 'video', width: 1080, height: 1920, metadata: { platform: 'tiktok', format: 'video', duration: 30 }, disabled: true },
];

// ── Default blank canvas SceneDocument ──

function makeBlankCanvasJson(width: number, height: number): SceneDocument {
  return {
    version: 2,
    width,
    height,
    nodes: {},
    rootChildren: [],
  };
}

// ── Template picker for "Add Frame" dialog ──

function AddTemplateSection({
  orgId,
  clientId,
  onSelect,
}: {
  orgId: string;
  clientId: string;
  onSelect: (
    templateId: string,
    name: string,
    mode: 'one-frame' | 'frame-per-section',
  ) => void;
}) {
  const { t } = useTranslation('common');
  const { data, isLoading } = useTemplatesControllerListTemplates(orgId, clientId, undefined, {
    query: { enabled: !!orgId && !!clientId },
  });

  const templates = data?.templates ?? [];

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
        <CircularProgress size={20} />
      </Box>
    );
  }

  if (templates.length === 0) {
    return (
      <Typography variant="body2" color="text.disabled" sx={{ py: 1.5, textAlign: 'center' }}>
        {t('workspaceId.addTemplate.empty', 'No templates yet. Generate an email first.')}
      </Typography>
    );
  }

  return (
    <Stack spacing={0.75} sx={{ pt: 0.5, maxHeight: 240, overflow: 'auto' }}>
      {templates.slice(0, 20).map((tpl) => (
        <Box
          key={tpl.id}
          sx={{
            p: 1.25,
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: '8px',
            display: 'flex',
            alignItems: 'center',
            gap: 1,
          }}
        >
          <ArticleIcon sx={{ fontSize: 18, color: 'info.main', opacity: 0.6 }} />
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="body2" fontWeight={600} noWrap>
              {tpl.name}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {tpl.subjectLine || t('workspaceId.addTemplate.noSubject', 'No subject')}
            </Typography>
          </Box>
          <Button
            size="small"
            variant="text"
            onClick={() => onSelect(tpl.id, tpl.name, 'one-frame')}
            sx={{ minWidth: 0, fontSize: '11px', textTransform: 'none' }}
          >
            {t('workspaceId.addTemplate.whole', 'Whole')}
          </Button>
          <Button
            size="small"
            variant="text"
            onClick={() => onSelect(tpl.id, tpl.name, 'frame-per-section')}
            sx={{ minWidth: 0, fontSize: '11px', textTransform: 'none' }}
          >
            {t('workspaceId.addTemplate.split', 'Split')}
          </Button>
        </Box>
      ))}
    </Stack>
  );
}

// ── Inner component (needs workspace store context) ──

function WorkspaceInner({
  workspaceId,
  orgId,
  clientId,
}: {
  workspaceId: string;
  orgId: string;
  clientId: string;
}) {
  const { t } = useTranslation('common');
  const navigate = useNavigate();
  const wsStoreApi = useWorkspaceStoreApi();
  const frameMeta = useWorkspaceStore((s) => s.frameMeta);
  const dirtyFrameIds = useWorkspaceStore((s) => s.dirtyFrameIds);

  const [snackbar, setSnackbar] = useState({ open: false, message: '' });
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [deleteFrameTarget, setDeleteFrameTarget] = useState<string | null>(null);
  const [openEmailFrame, setOpenEmailFrame] = useState<{
    templateId: string;
    sectionPath?: string;
    label: string;
  } | null>(null);
  const [rightTab, setRightTab] = useState<'properties' | 'chat'>('properties');
  const deleteFrameMeta = deleteFrameTarget ? frameMeta.get(deleteFrameTarget) : null;
  const [workspaceName, setWorkspaceName] = useState('');
  const [editingName, setEditingName] = useState(false);
  const saveTimeoutsRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());

  // Brand profile
  const { data: brandProfile } = useBrandsControllerGetClientBrandProfile(
    orgId, clientId, { query: { enabled: !!orgId && !!clientId } },
  );
  const brandColors = brandProfile?.colors?.map((c) => c.hexValue);
  const brandFonts = brandProfile?.fonts;

  // Set brand fonts for CanvasKit rendering
  useEffect(() => {
    if (!brandFonts?.length) {
      setFontsImportUrl(undefined);
      return;
    }
    // Collect explicit Google Font URLs from the brand profile
    const googleFontUrls = brandFonts
      .filter((f) => f.googleFontUrl)
      .map((f) => f.googleFontUrl!);
    if (googleFontUrls.length > 0) {
      // Use the first URL (they typically contain all families)
      setFontsImportUrl(googleFontUrls[0]);
    } else {
      // Only fall back to constructing Google Fonts URLs for non-custom fonts
      const googleFamilies = brandFonts
        .filter((f) => f.source !== 'shopify' && f.source !== 'system')
        .map((f) => f.fontFamily)
        .filter(Boolean) as string[];
      setFontFamilies(googleFamilies);
    }
  }, [brandFonts]);

  // Mutations
  const { mutate: updateWorkspace } = useWorkspacesControllerUpdateWorkspace({
    mutation: {
      onSuccess: () => setSnackbar({ open: true, message: t('workspaceId.snackbar.saved', 'Saved') }),
    },
  });

  const { mutate: updateCanvas } = useWorkspacesControllerUpdateCanvas();

  const { mutate: createCanvasMut } = useWorkspacesControllerCreateCanvas({
    mutation: {
      onSuccess: (data) => {
        const wsState = wsStoreApi.getState();

        // Calculate position for new frame (right of existing frames)
        const editorState = wsState.editorStore.getState();
        let maxRight = 0;
        for (const rootId of editorState.doc.rootChildren) {
          const n = editorState.doc.nodes[rootId];
          if (n) maxRight = Math.max(maxRight, n.x + n.width);
        }
        const newX = maxRight > 0 ? maxRight + 80 : 0;

        const canvasJson = (data.canvasJson as SceneDocument | undefined)
          || makeBlankCanvasJson(data.width, data.height);

        wsState.addFrame({
          canvasId: data.id,
          type: (data.type as 'email' | 'social_ad') || 'email',
          contentType: (data.contentType as 'canvas' | 'template') || 'canvas',
          label: data.label,
          width: data.width,
          height: data.height,
          x: newX,
          y: 0,
          canvasJson: data.contentType === 'template' ? undefined : canvasJson,
          templateId: data.templateId ?? undefined,
        });

        // Save x/y to DB
        updateCanvas({
          orgId, clientId, workspaceId,
          canvasId: data.id,
          data: { x: newX, y: 0 },
        });
      },
    },
  });

  const { mutate: deleteCanvasMut } = useWorkspacesControllerDeleteCanvas();

  const queryClient = useQueryClient();
  const { mutate: importFromTemplateMut } =
    useWorkspacesControllerImportFromTemplate({
      mutation: {
        onSuccess: (response) => {
          const wsState = wsStoreApi.getState();
          // Place imported frames to the right of existing ones, stacked
          // vertically — matches the server's per-section layout intent.
          const editorState = wsState.editorStore.getState();
          let maxRight = 0;
          for (const rootId of editorState.doc.rootChildren) {
            const n = editorState.doc.nodes[rootId];
            if (n) maxRight = Math.max(maxRight, n.x + n.width);
          }
          const baseX = maxRight > 0 ? maxRight + 80 : 0;
          let cursorY = 0;
          for (const c of response.canvases) {
            wsState.addFrame({
              canvasId: c.id,
              type: 'email',
              contentType: 'template',
              label: c.label,
              width: c.width,
              height: c.height,
              x: baseX,
              y: cursorY,
              templateId: c.templateId,
              sectionPath: (c.metadata as { sectionPath?: string } | undefined)
                ?.sectionPath,
            });
            // Persist the local position back to the server so a refresh
            // shows the same layout.
            updateCanvas({
              orgId,
              clientId,
              workspaceId,
              canvasId: c.id,
              data: { x: baseX, y: cursorY },
            });
            cursorY += c.height + 80;
          }
          queryClient.invalidateQueries({
            queryKey: getWorkspacesControllerGetWorkspaceQueryKey(
              orgId,
              clientId,
              workspaceId,
            ),
          });
          setSnackbar({
            open: true,
            message: t('workspaceId.snackbar.imported', 'Imported {{count}} frame', { count: response.canvases.length }),
          });
        },
      },
    });

  // ── Auto-save: subscribe to editor changes ──

  // Helper: save a single frame by extracting its subtree
  const saveFrame = useCallback(
    (canvasId: string) => {
      const wsState = wsStoreApi.getState();
      // Find the frame nodeId for this canvasId
      for (const [nodeId, meta] of wsState.frameMeta) {
        if (meta.canvasId === canvasId) {
          const frameDoc = wsState.extractFrameDoc(nodeId);
          if (frameDoc) {
            updateCanvas({
              orgId, clientId, workspaceId,
              canvasId,
              data: { canvasJson: frameDoc as any },
            });
          }
          break;
        }
      }
    },
    [wsStoreApi, orgId, clientId, workspaceId, updateCanvas],
  );

  // Auto-save frame content when dirty flags change
  useEffect(() => {
    const unsub = wsStoreApi.subscribe((state, prevState) => {
      if (state.dirtyFrameIds !== prevState.dirtyFrameIds) {
        for (const canvasId of state.dirtyFrameIds) {
          if (prevState.dirtyFrameIds.has(canvasId)) continue;

          const existing = saveTimeoutsRef.current.get(canvasId);
          if (existing) clearTimeout(existing);

          saveTimeoutsRef.current.set(
            canvasId,
            setTimeout(() => {
              saveFrame(canvasId);
              wsStoreApi.getState().clearFrameDirty(canvasId);
              saveTimeoutsRef.current.delete(canvasId);
            }, 2000),
          );
        }
      }
    });
    return () => unsub();
  }, [wsStoreApi, saveFrame]);

  // Auto-save frame positions when workspace layout changes
  useEffect(() => {
    let positionTimeout: ReturnType<typeof setTimeout> | null = null;
    const unsub = wsStoreApi.subscribe((state, prevState) => {
      if (state.workspaceDirty && !prevState.workspaceDirty) {
        if (positionTimeout) clearTimeout(positionTimeout);
        positionTimeout = setTimeout(() => {
          const wsState = wsStoreApi.getState();
          const { doc } = wsState.editorStore.getState();
          for (const rootId of doc.rootChildren) {
            const node = doc.nodes[rootId] as import('@/lib/scene-document').SceneNode | undefined;
            if (!node?.frameCanvasId) continue;
            updateCanvas({
              orgId, clientId, workspaceId,
              canvasId: node.frameCanvasId,
              data: { x: Math.round(node.x), y: Math.round(node.y) },
            });
          }
          wsState.clearWorkspaceDirty();
        }, 3000);
      }
    });
    return () => {
      unsub();
      if (positionTimeout) clearTimeout(positionTimeout);
    };
  }, [wsStoreApi, orgId, clientId, workspaceId, updateCanvas]);

  // Auto-mark all frames dirty when editor doc changes
  useEffect(() => {
    const editorStore = wsStoreApi.getState().editorStore;
    let lastVersion = editorStore.getState().renderVersion;
    const unsub = editorStore.subscribe((state) => {
      if (state.renderVersion !== lastVersion) {
        lastVersion = state.renderVersion;
        const wsState = wsStoreApi.getState();
        // Mark all frames as dirty (simple approach; could optimize later)
        for (const meta of wsState.frameMeta.values()) {
          wsState.markFrameDirty(meta.canvasId);
        }
        wsState.markWorkspaceDirty();
      }
    });
    return unsub;
  }, [wsStoreApi]);

  // Flush pending saves on unmount
  useEffect(() => 
     () => {
      saveTimeoutsRef.current.forEach((timer) => clearTimeout(timer));
      const wsState = wsStoreApi.getState();
      for (const canvasId of wsState.dirtyFrameIds) {
        saveFrame(canvasId);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  , []);

  // beforeunload warning
  useEffect(() => {
    const handler = (e: BeforeUnloadEvent) => {
      if (wsStoreApi.getState().dirtyFrameIds.size > 0) {
        e.preventDefault();
      }
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [wsStoreApi]);

  // ── Add frame handlers ──

  const handleAddCanvas = useCallback(
    (preset: CanvasPreset) => {
      setAddDialogOpen(false);
      createCanvasMut({
        orgId, clientId, workspaceId,
        data: {
          type: preset.type,
          label: preset.label,
          width: preset.width,
          height: preset.height,
          canvasJson: makeBlankCanvasJson(preset.width, preset.height) as any,
          metadata: preset.metadata,
        },
      });
    },
    [orgId, clientId, workspaceId, createCanvasMut],
  );

  const handleAddTemplate = useCallback(
    (
      templateId: string,
      templateName: string,
      mode: 'one-frame' | 'frame-per-section',
    ) => {
      setAddDialogOpen(false);
      if (mode === 'frame-per-section') {
        importFromTemplateMut({
          orgId,
          clientId,
          workspaceId,
          data: { templateId, mode },
        });
        return;
      }
      createCanvasMut({
        orgId,
        clientId,
        workspaceId,
        data: {
          type: 'email',
          contentType: 'template',
          templateId,
          label: templateName || t('workspaceId.emailTemplateFallbackLabel', 'Email Template'),
        },
      });
    },
    [orgId, clientId, workspaceId, createCanvasMut, importFromTemplateMut],
  );

  // Save all
  const handleSaveAll = useCallback(() => {
    saveTimeoutsRef.current.forEach((timer) => clearTimeout(timer));
    const wsState = wsStoreApi.getState();
    for (const canvasId of wsState.dirtyFrameIds) {
      saveFrame(canvasId);
    }
    // Clear all dirty flags
    for (const canvasId of [...wsState.dirtyFrameIds]) {
      wsState.clearFrameDirty(canvasId);
    }
    setSnackbar({ open: true, message: t('workspaceId.snackbar.allFramesSaved', 'All frames saved') });
  }, [wsStoreApi, saveFrame, t]);

  // Save workspace name
  const handleNameSave = useCallback(() => {
    setEditingName(false);
    if (workspaceName.trim()) {
      updateWorkspace({
        orgId, clientId, workspaceId,
        data: { name: workspaceName.trim() },
      });
    }
  }, [orgId, clientId, workspaceId, workspaceName, updateWorkspace]);

  const frameCount = frameMeta.size;
  const hasDirty = dirtyFrameIds.size > 0;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh' }}>
      {/* Header bar */}
      <Box
        sx={{
          height: 56,
          px: 2,
          display: 'flex',
          alignItems: 'center',
          borderBottom: '1px solid',
          borderColor: 'divider',
          bgcolor: 'background.paper',
          flexShrink: 0,
          gap: 1,
        }}
      >
        <IconButton
          size="small"
          onClick={() => navigate({ to: paths.dashboard.tenant(orgId, clientId).workspaces })}
        >
          <ArrowBackIcon />
        </IconButton>

        {editingName ? (
          <TextField
            value={workspaceName}
            onChange={(e) => setWorkspaceName(e.target.value)}
            onBlur={handleNameSave}
            onKeyDown={(e) => { if (e.key === 'Enter') handleNameSave(); }}
            size="small"
            variant="standard"
            autoFocus
            sx={{ maxWidth: 300, '& .MuiInput-input': { fontSize: '16px', fontWeight: 600 } }}
          />
        ) : (
          <Typography
            variant="subtitle1"
            onClick={() => setEditingName(true)}
            sx={{ fontWeight: 600, cursor: 'pointer', '&:hover': { color: 'primary.main' } }}
          >
            {workspaceName || t('workspaceId.untitledWorkspace', 'Untitled Workspace')}
          </Typography>
        )}

        <Typography variant="caption" color="text.disabled" sx={{ ml: 1 }}>
          {t('workspaceId.frameCount', '{{count}} frame', { count: frameCount })}
        </Typography>

        {/* Editing indicator removed — single canvas model */}

        <Box sx={{ flex: 1 }} />

        <Button
          size="small"
          variant="outlined"
          startIcon={<AddIcon />}
          onClick={() => setAddDialogOpen(true)}
          sx={{ mr: 1 }}
        >
          {t('workspaceId.addFrame', 'Add Frame')}
        </Button>

        <Button
          size="small"
          variant="outlined"
          startIcon={<SaveIcon />}
          disabled={!hasDirty}
          onClick={handleSaveAll}
        >
          {t('workspaceId.saveAll', 'Save All')}
        </Button>
      </Box>

      {/* Body */}
      <Box sx={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
        <WorkspaceToolbar brandColors={brandColors} onRequestDeleteFrame={setDeleteFrameTarget} />

        <WorkspaceInfiniteCanvas
          onRequestDeleteFrame={setDeleteFrameTarget}
          onRequestOpenEmailFrame={setOpenEmailFrame}
        />

        {/* Right panel: tabbed Properties (per-selection) | Chat (workspace
            orchestrator). Always docked; no hidden state. */}
        <Box
          sx={{
            width: 320,
            borderLeft: '1px solid',
            borderColor: 'divider',
            bgcolor: 'background.paper',
            display: 'flex',
            flexDirection: 'column',
            flexShrink: 0,
          }}
        >
          <Tabs
            value={rightTab}
            onChange={(_, v) => setRightTab(v)}
            variant="fullWidth"
            sx={{
              minHeight: 36,
              borderBottom: '1px solid',
              borderColor: 'divider',
              '& .MuiTab-root': {
                minHeight: 36,
                textTransform: 'none',
                fontSize: 12,
                fontWeight: 600,
                py: 0,
              },
            }}
          >
            <Tab value="properties" label={t('workspaceId.tabs.properties', 'Properties')} />
            <Tab value="chat" label={t('workspaceId.tabs.chat', 'Chat')} />
          </Tabs>
          {rightTab === 'properties' ? (
            <Box sx={{ flex: 1, overflow: 'auto' }}>
              <ScenePropertiesPanel brandColors={brandColors} />
            </Box>
          ) : (
            <Box sx={{ flex: 1, overflow: 'hidden' }}>
              <WorkspaceChatPanel workspaceId={workspaceId} />
            </Box>
          )}
        </Box>
      </Box>

      {/* Email frame editor: full-bleed overlay backed by the workspace-frame
          mode controller. The workspace canvas keeps its state mounted
          underneath so closing returns the user exactly where they were. */}
      {openEmailFrame && (
        <Box
          sx={{
            position: 'fixed',
            inset: 0,
            bgcolor: 'background.default',
            zIndex: 1200,
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <Box
            sx={{
              height: 56,
              px: 2,
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              borderBottom: '1px solid',
              borderColor: 'divider',
              bgcolor: 'background.paper',
              flexShrink: 0,
            }}
          >
            <IconButton size="small" onClick={() => setOpenEmailFrame(null)}>
              <ArrowBackIcon />
            </IconButton>
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              {openEmailFrame.label}
            </Typography>
            <Typography variant="caption" color="text.disabled" sx={{ ml: 1 }}>
              {openEmailFrame.sectionPath
                ? t('workspaceId.editingEmailSection', 'Editing email section')
                : t('workspaceId.editingEmail', 'Editing email')}
            </Typography>
          </Box>
          <Box sx={{ flex: 1, overflow: 'hidden' }}>
            <EmailBuilder
              mode="workspace-frame"
              templateId={openEmailFrame.templateId}
              focusSectionId={openEmailFrame.sectionPath}
            />
          </Box>
        </Box>
      )}

      {/* Add frame dialog */}
      <Dialog open={addDialogOpen} onClose={() => setAddDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>{t('workspaceId.addFrameDialog.title', 'Add Frame')}</DialogTitle>
        <DialogContent>
          <Typography variant="overline" color="text.secondary" sx={{ fontSize: '10px', letterSpacing: 1 }}>
            {t('workspaceId.addFrameDialog.newCanvas', 'New Canvas')}
          </Typography>
          <Stack spacing={1} sx={{ pt: 0.5, pb: 2 }}>
            {CANVAS_PRESETS.map((preset) => (
              <Box
                key={preset.label}
                onClick={() => { if (!preset.disabled) handleAddCanvas(preset); }}
                sx={{
                  p: 1.5,
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: '8px',
                  cursor: preset.disabled ? 'default' : 'pointer',
                  opacity: preset.disabled ? 0.4 : 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  '&:hover': preset.disabled ? {} : { borderColor: 'primary.main', bgcolor: 'primary.50' },
                }}
              >
                <Box>
                  <Typography variant="body2" fontWeight={600}>
                    {preset.label}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {preset.width} x {preset.height}
                  </Typography>
                </Box>
                <Chip
                  label={preset.type === 'video' ? t('workspaceId.addFrameDialog.comingSoon', 'Coming Soon') : preset.type.replace('_', ' ')}
                  size="small"
                  sx={{ height: 20, fontSize: '10px' }}
                />
              </Box>
            ))}
          </Stack>

          <Divider />

          <Typography variant="overline" color="text.secondary" sx={{ fontSize: '10px', letterSpacing: 1, mt: 2, display: 'block' }}>
            {t('workspaceId.addFrameDialog.linkEmailTemplate', 'Link Email Template')}
          </Typography>
          <AddTemplateSection
            orgId={orgId}
            clientId={clientId}
            onSelect={handleAddTemplate}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddDialogOpen(false)}>{t('common:cancel')}</Button>
        </DialogActions>
      </Dialog>

      {/* Delete frame confirmation */}
      <Dialog
        open={deleteFrameTarget !== null}
        onClose={() => setDeleteFrameTarget(null)}
        maxWidth="xs"
      >
        <DialogTitle sx={{ fontSize: 16 }}>{t('workspaceId.deleteDialog.title', 'Delete frame')}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            {t('workspaceId.deleteDialog.body', 'Delete "{{label}}"? This cannot be undone.', { label: deleteFrameMeta?.label })}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button size="small" onClick={() => setDeleteFrameTarget(null)}>
            {t('common:cancel')}
          </Button>
          <Button
            size="small"
            color="error"
            variant="contained"
            onClick={() => {
              if (deleteFrameTarget) {
                wsStoreApi.getState().removeFrame(deleteFrameTarget);
              }
              setDeleteFrameTarget(null);
            }}
          >
            {t('common:delete')}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={2000}
        onClose={() => setSnackbar({ open: false, message: '' })}
        message={snackbar.message}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </Box>
  );
}

// ── Page wrapper (loads data, builds workspace doc, provides context) ──

function WorkspacePage() {
  const { workspaceId } = Route.useParams();
  const { activeOrganization, activeClient } = useOrganization();
  const orgId = activeOrganization?.id || '';
  const clientId = activeClient?.id || '';

  const { data: workspace, isLoading } = useWorkspacesControllerGetWorkspace(
    orgId,
    clientId,
    workspaceId,
    { query: { enabled: !!orgId && !!clientId && !!workspaceId } },
  );

  // Build merged workspace doc from API data
  const initData = useMemo(() => {
    if (!workspace) return null;

    const canvases = (workspace.canvases || []).map((c) => ({
      id: c.id,
      type: (c.type as 'email' | 'social_ad' | 'video') || 'email',
      contentType: (c.contentType as 'canvas' | 'template') || 'canvas',
      label: c.label,
      width: c.width,
      height: c.height,
      x: c.x ?? 0,
      y: c.y ?? 0,
      canvasJson:
        c.contentType === 'template'
          ? null
          : c.canvasJson
            ? (c.canvasJson as unknown as SceneDocument)
            : makeBlankCanvasJson(c.width, c.height),
      templateId: c.templateId,
      sectionPath: (c.metadata as { sectionPath?: string } | undefined)
        ?.sectionPath,
    }));

    const { doc, meta } = mergeWorkspaceToSingleDoc(canvases);
    return { doc, meta };
  }, [workspace]);

  if (isLoading || !workspace || !initData) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <WorkspaceEditorProvider
      initialDoc={initData.doc}
      initialMeta={initData.meta}
    >
      <WorkspaceInner workspaceId={workspaceId} orgId={orgId} clientId={clientId} />
    </WorkspaceEditorProvider>
  );
}
